"use client";

// src/ui/components/calculator/calculator-actions.tsx
//
// Actions + Share for the Peptidrechner (handoff 02 §1.10). Two buttons:
//   • "Speichern" — opens the existing ProtocolModal. Persists to the user's
//     account when signed in (same /api/calculator/protocols path SavedProtocols
//     uses), else a guest entry in localStorage `bb-pr-protocols-v1` (kind "single").
//   • "Teilen" — opens a share-sheet (bottom-sheet on mobile, popover on desktop)
//     with a prefilled share URL (?p&vial&bac&dose&unit), a "Link kopieren" button,
//     and a deterministic QR (qrcode.react, already a dependency — no new dep).
//
// The share URL is a real calculator URL with query params that the hook reads back
// on load (see use-calculator-state.ts readShareParams), so a shared link reopens
// the calculator exactly as shared.

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { QRCodeSVG } from "qrcode.react";
import { Save, Share2, Copy, Check, X } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { ProtocolModal } from "@/ui/components/calculator/protocol-modal";
import {
	saveGuestProtocol,
	loadGuestProtocols,
	GUEST_PROTOCOLS_MAX,
} from "@/lib/calculator/calculator-storage";
import { encodeCookieName } from "@/lib/auth/constants";
import type { DisplayUnit } from "@/lib/calculator/types";

const SALEOR_API_URL = process.env.NEXT_PUBLIC_SALEOR_API_URL ?? "";

/** Cookie presence check (client-side) for the Saleor access token. */
function hasSaleorToken(): boolean {
	if (typeof document === "undefined") return false;
	const cookieName = encodeCookieName(`${SALEOR_API_URL}+saleor_auth_access_token`);
	return document.cookie.includes(cookieName);
}

interface CalculatorActionsValues {
	peptideSlug: string;
	peptideName: string;
	peptideAmount: number;
	bacWater: number;
	dose: number;
	syringeSize: number;
	displayUnit: DisplayUnit;
}

interface CalculatorActionsProps {
	values: CalculatorActionsValues;
}

export function CalculatorActions({ values }: CalculatorActionsProps) {
	const t = useTranslations("peptideCalculator");
	const [saveOpen, setSaveOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	const [shareOpen, setShareOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const shareWrapRef = useRef<HTMLDivElement>(null);

	// Build the share URL from the live calculator state. Same-origin so it survives
	// channel/locale; the hook reads ?p&vial&bac&dose&unit back on load.
	const shareUrl = useMemo(() => {
		if (typeof window === "undefined") return "";
		const url = new URL(window.location.href);
		// Drop any inherited params, then stamp the calculator state.
		url.search = "";
		url.searchParams.set("p", values.peptideSlug);
		url.searchParams.set("vial", String(values.peptideAmount));
		url.searchParams.set("bac", String(values.bacWater));
		url.searchParams.set("dose", String(values.dose));
		url.searchParams.set("unit", values.displayUnit);
		return url.toString();
	}, [values.peptideSlug, values.peptideAmount, values.bacWater, values.dose, values.displayUnit]);

	// Close the desktop share popover on outside-click / Esc.
	useEffect(() => {
		if (!shareOpen) return;
		function onDown(e: MouseEvent) {
			if (shareWrapRef.current && !shareWrapRef.current.contains(e.target as Node)) {
				setShareOpen(false);
			}
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === "Escape") setShareOpen(false);
		}
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [shareOpen]);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(shareUrl);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 1600);
		} catch {
			// Clipboard can be blocked (insecure context / permissions) — fail silently.
		}
	};

	const handleSave = async (name: string, notes: string) => {
		setSaving(true);
		try {
			if (hasSaleorToken()) {
				// Account path — same endpoint + payload shape as SavedProtocols.
				await fetch("/api/calculator/protocols", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						name,
						notes: notes || undefined,
						peptideSlug: values.peptideSlug,
						peptideAmount: values.peptideAmount,
						bacWater: values.bacWater,
						dose: values.dose,
						syringeSize: values.syringeSize,
					}),
				});
			} else {
				// Guest path — localStorage via the shared store helper (capped, record-
				// shaped so guest + account surfaces stay uniform).
				if (loadGuestProtocols().length >= GUEST_PROTOCOLS_MAX) {
					if (typeof window !== "undefined") window.alert(t("actions.maxReached"));
					return;
				}
				saveGuestProtocol({
					name,
					notes: notes || undefined,
					peptideSlug: values.peptideSlug,
					peptideAmount: values.peptideAmount,
					bacWater: values.bacWater,
					dose: values.dose,
					syringeSize: values.syringeSize,
				});
			}
			setSaveOpen(false);
		} catch {
			// Persistence is best-effort — keep the modal open on a hard failure is
			// noisier than just closing; close to avoid a stuck spinner.
			setSaveOpen(false);
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="relative" ref={shareWrapRef}>
			<div className="grid grid-cols-2 gap-2">
				<Button
					variant="outline-solid"
					onClick={() => setSaveOpen(true)}
					disabled={saving}
					className="min-h-11 w-full gap-2"
				>
					<Save className="h-4 w-4" />
					{t("actions.save")}
				</Button>
				<Button
					variant="outline-solid"
					onClick={() => setShareOpen((v) => !v)}
					aria-expanded={shareOpen}
					aria-haspopup="dialog"
					className="min-h-11 w-full gap-2"
				>
					<Share2 className="h-4 w-4" />
					{t("actions.share")}
				</Button>
			</div>

			{/* Share sheet: bottom-sheet on mobile (fixed), popover on desktop (absolute). */}
			{shareOpen && (
				<>
					{/* Mobile scrim */}
					<div
						className="fixed inset-0 z-40 bg-black/50 sm:hidden"
						aria-hidden
						onClick={() => setShareOpen(false)}
					/>
					<div
						role="dialog"
						aria-label={t("actions.shareTitle")}
						className="motion-safe:duration-[0.2s] fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border border-border bg-background p-4 shadow-xl motion-safe:animate-in motion-safe:slide-in-from-bottom-2 sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-0 sm:top-full sm:z-50 sm:mt-2 sm:w-80 sm:rounded-xl"
					>
						<div className="flex items-start justify-between gap-2">
							<div>
								<p className="text-sm font-semibold text-foreground">{t("actions.shareTitle")}</p>
								<p className="mt-0.5 text-xs text-muted-foreground">{t("actions.shareSub")}</p>
							</div>
							<button
								type="button"
								onClick={() => setShareOpen(false)}
								aria-label={t("actions.close")}
								className="-mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
							>
								<X className="h-4 w-4" />
							</button>
						</div>

						<div className="mt-3 flex items-center gap-3">
							<div className="shrink-0 rounded-lg border border-border bg-card p-2">
								<QRCodeSVG value={shareUrl} size={84} level="M" />
							</div>
							<div className="flex min-w-0 flex-1 flex-col gap-2">
								<p className="truncate rounded-md border border-border bg-muted px-2 py-1.5 font-mono text-[11px] text-muted-foreground">
									{shareUrl}
								</p>
								<Button
									size="sm"
									onClick={() => void handleCopy()}
									className="w-full gap-2"
									aria-live="polite"
								>
									<span className="t-icon-swap h-4 w-4" data-state={copied ? "b" : "a"} aria-hidden="true">
										<span className="t-icon" data-icon="a">
											<Copy className="h-4 w-4" />
										</span>
										<span className="t-icon" data-icon="b">
											<Check className="h-4 w-4" />
										</span>
									</span>
									{copied ? t("actions.copied") : t("actions.copyLink")}
								</Button>
							</div>
						</div>
					</div>
				</>
			)}

			{/* Save modal — reuses the existing ProtocolModal. */}
			<ProtocolModal
				open={saveOpen && !saving}
				onClose={() => setSaveOpen(false)}
				onSave={(name, notes) => void handleSave(name, notes)}
			/>
		</div>
	);
}
